import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Users, Mail, BookOpen, Eye, Search, 
  TrendingUp, DollarSign, Crown, RefreshCw,
  Filter, ChevronDown, UserCheck, Star,
  ArrowUpDown, Calendar, Trash2, Loader2
} from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import api from '../../lib/api';

const AdminUsers = () => {
  const [data, setData] = useState({ users: [], metrics: {} });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all'); // all, premium, with_purchases, new
  const [sortBy, setSortBy] = useState('ltv'); // ltv, name, date
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const response = await api.get('/admin/crm');
      setData(response.data);
    } catch (error) {
      console.error('Error fetching CRM data:', error);
      // Fallback to old endpoint
      try {
        const fallback = await api.get('/admin/users');
        setData({ 
          users: fallback.data.map(u => ({ ...u, ltv: 0, courses_purchased: [] })), 
          metrics: { total_users: fallback.data.length } 
        });
      } catch (e) {
        console.error('Fallback also failed:', e);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async (user) => {
    if (!window.confirm(`¿Eliminar permanentemente a ${user.name || user.email}?\n\nEsto eliminará:\n- El usuario\n- Sus inscripciones\n- Sus pagos\n- Su contacto en CRM\n\nEsta acción no se puede deshacer.`)) {
      return;
    }
    setDeletingId(user.id);
    try {
      await api.delete(`/admin/users/${user.id}`);
      toast.success(`Usuario ${user.name || user.email} eliminado`);
      setData(prev => ({
        ...prev,
        users: prev.users.filter(u => u.id !== user.id)
      }));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar usuario');
    } finally {
      setDeletingId(null);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('es-ES', { 
      day: 'numeric', 
      month: 'short', 
      year: 'numeric' 
    });
  };

  // Filter and sort users
  let filteredUsers = data.users.filter(user => {
    const matchesSearch = 
      user.name?.toLowerCase().includes(search.toLowerCase()) ||
      user.email?.toLowerCase().includes(search.toLowerCase());
    
    if (!matchesSearch) return false;
    
    switch (filter) {
      case 'premium': return user.is_premium;
      case 'with_purchases': return user.ltv > 0;
      case 'new': return user.ltv === 0;
      default: return true;
    }
  });

  // Sort
  filteredUsers = [...filteredUsers].sort((a, b) => {
    switch (sortBy) {
      case 'ltv': return b.ltv - a.ltv;
      case 'name': return (a.name || '').localeCompare(b.name || '');
      case 'date': return new Date(b.created_at) - new Date(a.created_at);
      default: return 0;
    }
  });

  const metrics = data.metrics || {};

  return (
    <div className="p-6 lg:p-8">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-slate-900" data-testid="crm-title">
          CRM Alumnos
        </h1>
        <p className="text-slate-500 text-sm mt-1">
          Gestión de clientes y análisis de valor
        </p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-testid="crm-metrics">
        <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-100">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium text-slate-400 uppercase">LTV Promedio</span>
            <DollarSign className="w-4 h-4 text-emerald-500" />
          </div>
          <p className="text-2xl font-semibold text-slate-900">{formatCurrency(metrics.avg_ltv)}</p>
          <p className="text-xs text-slate-400 mt-1">Por cliente con compras</p>
        </div>

        <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-100">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium text-slate-400 uppercase">% Repetición</span>
            <RefreshCw className="w-4 h-4 text-blue-500" />
          </div>
          <p className="text-2xl font-semibold text-slate-900">{metrics.repeat_rate}%</p>
          <p className="text-xs text-slate-400 mt-1">Clientes recurrentes</p>
        </div>

        <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-100">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium text-slate-400 uppercase">Clientes Premium</span>
            <Crown className="w-4 h-4 text-amber-500" />
          </div>
          <p className="text-2xl font-semibold text-slate-900">{metrics.premium_count || 0}</p>
          <p className="text-xs text-slate-400 mt-1">{metrics.premium_rate}% del total (LTV &gt;500€)</p>
        </div>

        <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-100">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium text-slate-400 uppercase">Nuevos vs Recurrentes</span>
            <Users className="w-4 h-4 text-purple-500" />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-lg font-semibold text-slate-900">{metrics.new_vs_recurring?.new || 0}</span>
            <span className="text-slate-400">/</span>
            <span className="text-lg font-semibold text-emerald-600">{metrics.new_vs_recurring?.recurring || 0}</span>
          </div>
          <p className="text-xs text-slate-400 mt-1">Nuevos / Recurrentes</p>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-100 mb-6">
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Search */}
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Buscar por nombre o email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
              data-testid="search-users"
            />
          </div>

          {/* Filter */}
          <div className="flex gap-2">
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="px-3 py-2 rounded-lg border border-slate-200 text-sm bg-white"
            >
              <option value="all">Todos ({data.users.length})</option>
              <option value="premium">Premium ({data.users.filter(u => u.is_premium).length})</option>
              <option value="with_purchases">Con compras ({data.users.filter(u => u.ltv > 0).length})</option>
              <option value="new">Sin compras ({data.users.filter(u => u.ltv === 0).length})</option>
            </select>

            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-3 py-2 rounded-lg border border-slate-200 text-sm bg-white"
            >
              <option value="ltv">Ordenar por LTV</option>
              <option value="name">Ordenar por nombre</option>
              <option value="date">Ordenar por fecha</option>
            </select>
          </div>
        </div>
      </div>

      {/* Users Table */}
      {loading ? (
        <div className="bg-white rounded-xl p-12 text-center">
          <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-500">Cargando alumnos...</p>
        </div>
      ) : filteredUsers.length === 0 ? (
        <div className="bg-white rounded-xl p-12 text-center">
          <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500">
            {search ? 'No se encontraron resultados' : 'No hay alumnos registrados'}
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-50">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase tracking-wide">Alumno</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase tracking-wide hidden md:table-cell">Cursos</th>
                <th className="text-right px-4 py-3 text-xs font-medium text-slate-500 uppercase tracking-wide">LTV</th>
                <th className="text-center px-4 py-3 text-xs font-medium text-slate-500 uppercase tracking-wide hidden lg:table-cell">Última compra</th>
                <th className="text-center px-4 py-3 text-xs font-medium text-slate-500 uppercase tracking-wide">Estado</th>
                <th className="text-right px-4 py-3 text-xs font-medium text-slate-500 uppercase tracking-wide">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredUsers.map((user, index) => (
                <tr key={user.id} className="hover:bg-slate-50/50" data-testid={`user-row-${index}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                        user.is_premium ? 'bg-amber-100' : 'bg-slate-100'
                      }`}>
                        {user.is_premium ? (
                          <Crown className="w-5 h-5 text-amber-600" />
                        ) : (
                          <span className="text-slate-600 font-medium">
                            {user.name?.charAt(0)?.toUpperCase()}
                          </span>
                        )}
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium text-slate-900 truncate flex items-center gap-1">
                          {user.name}
                          {user.is_premium && <Star className="w-3 h-3 text-amber-500" />}
                        </p>
                        <p className="text-sm text-slate-400 truncate">{user.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell">
                    <div className="flex flex-wrap gap-1">
                      {user.courses_purchased?.slice(0, 2).map((course, i) => (
                        <span key={i} className="px-2 py-0.5 bg-indigo-50 text-indigo-700 text-xs rounded-full truncate max-w-[120px]">
                          {course}
                        </span>
                      ))}
                      {user.courses_purchased?.length > 2 && (
                        <span className="px-2 py-0.5 bg-slate-100 text-slate-500 text-xs rounded-full">
                          +{user.courses_purchased.length - 2}
                        </span>
                      )}
                      {(!user.courses_purchased || user.courses_purchased.length === 0) && (
                        <span className="text-xs text-slate-400">-</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span className={`font-semibold ${user.ltv > 0 ? 'text-emerald-600' : 'text-slate-400'}`}>
                      {formatCurrency(user.ltv)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center hidden lg:table-cell">
                    <span className="text-sm text-slate-500">{formatDate(user.last_purchase)}</span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                      user.is_premium ? 'bg-amber-100 text-amber-700' :
                      user.ltv > 0 ? 'bg-green-100 text-green-700' :
                      'bg-slate-100 text-slate-500'
                    }`}>
                      {user.is_premium ? (
                        <><Crown className="w-3 h-3" /> Premium</>
                      ) : user.ltv > 0 ? (
                        <><UserCheck className="w-3 h-3" /> Cliente</>
                      ) : (
                        'Lead'
                      )}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Link 
                        to={`/admin/alumnos/${user.id}`}
                        className="inline-flex items-center gap-1 px-3 py-1.5 text-sm text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                      >
                        <Eye className="w-4 h-4" />
                        <span className="hidden sm:inline">Ver</span>
                      </Link>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDeleteUser(user); }}
                        disabled={deletingId === user.id}
                        className="inline-flex items-center p-1.5 text-red-500 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                        title="Eliminar alumno"
                      >
                        {deletingId === user.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Trash2 className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Summary */}
      <div className="mt-4 text-center text-sm text-slate-400">
        Mostrando {filteredUsers.length} de {data.users.length} alumnos · 
        LTV total: {formatCurrency(metrics.total_ltv)}
      </div>
    </div>
  );
};

export default AdminUsers;
